import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

/* UI + feature modules (unchanged) … */
import {
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';

import { CommonModule } from '@angular/common';
import {
  NameDialogComponent,
  NameDialogConfig,
} from '../common/popups/name-popup/name-popup.component';

/* 👉 NEW – API helpers */
import { HttpService } from '../services/http.service';
import { DataService } from '../services/data.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProgressBarLoaderComponent } from "../common/progress-bar-loader/progress-bar-loader.component";
import { ConfirmDeletePopupComponent } from '../common/popups/confirm-delete-popup/confirm-delete-popup.component';

interface Project {
  projectId: string;
  projectName: string;
  createdAt: string;
  countOfReports: number;
  countOfDoneReports: number;
  countOfErrorReports: number;
}

@Component({
  selector: 'app-home-component',
  standalone: true,
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatGridListModule,
    MatListModule,
    MatDialogModule,
    NameDialogComponent,
    MatProgressSpinnerModule,
    ProgressBarLoaderComponent
],
})
export class HomeComponent implements OnInit {
  /* ------------------------------------------------------------------ */
  private http = inject(HttpService);
  private data = inject(DataService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  userId:any = '';
  projects = signal<Project[]>([]);
  view = signal<'tile' | 'list'>('tile');
  selected = signal<Project | null>(null);
  loading=false;
  loadingText:any=null;
  isError:any=false;
  /* ------------------ life-cycle ------------------ */
  async ngOnInit(): Promise<void> {
    this.userId = this.data.username;          // however you store it
    console.log('is logged in', this.data.isLoggedIn())
    this.data.postLoginFunction=this.loadProjects.bind(this); 
    if(this.data.isLoggedIn())
    {
      await this.loadProjects();
    }
  }

  /* ------------------ helpers --------------------- */
  private async loadProjects(): Promise<void> {
    this.loading=true;
    this.loadingText=null;
    this.userId = this.data.username;          // however you store it


    // inside loadProjects(), replace the placeholder with:
    this.http
      .get(`/projects?userId=${encodeURIComponent(this.userId)}`)
      .then((res) => {
        this.loading = false
        this.projects.set(res)
      })
      .catch((err) => {
        this.isError = true
        console.error('Failed to load projects', err)
      })
  }

  /* ================== CRUD actions ================= */

  /* CREATE */
  openCreateProject(nameError:any=null): void {
    this.dialog
      .open<NameDialogComponent, NameDialogConfig, string>(NameDialogComponent, {
        width: '560px',
        data: { title: 'New Project', initialName: null, nameError:nameError },
      })
      .afterClosed()
      .subscribe(async (name) => {
        if (!name) return;
        // Prevent duplicate project names (case‐insensitive)
        if (this.projects().some(p => p.projectName.toLowerCase() === name.toLowerCase())) {
          // signal an error or notify the user
          this.openCreateProject('Project name already exists. Please choose a different name.');
          console.error('Project name already exists');
          return;
        }

        this.loadingText = 'Creating project...';
        this.loading = true;

        this.http
          .post('/project', {
            userId: this.userId,
            projectName: name,
          })
          .then((res) => {
            this.loading = false;
            console.log('res', res, res.projectId);
            this.redirectToReportWithId(res.projectId);
          })
          .catch((err) => {
            this.loading = false;
            this.isError = true;
            console.error('Failed to create project', err);
          });
      });
  }

  /* RENAME */
  renameProject(p: Project | null, nameError:any=null): void {
    if (!p) return;
    this.dialog
      .open<NameDialogComponent, NameDialogConfig, string>(NameDialogComponent, {
        width: '460px',
        data: { title: 'Rename Project', initialName: p.projectName, nameError:nameError},
      })
      .afterClosed()
      .subscribe(async (newName) => {
        if (!newName || newName === p.projectName) return;
        console.log('check proejct exists',this.projects().some(x => x.projectName.toLowerCase() === newName.toLowerCase()))
        if( this.projects().some(x => x.projectName.toLowerCase() === newName.toLowerCase())) {
          // signal an error or notify the user
          this.renameProject(p, 'Project name already exists. Please choose a different name.');
          console.error('Project name already exists');
          return;
        }
        this.http
          .put('/project/rename', {
            userId: this.userId,
            projectId: p.projectId,
            newName,
          })
          .then(() => {
            // rename the project in the list
            this.projects.set(
              this.projects().map(x =>
                x === p ? { ...x, projectName: newName } : x
              )
            );
          })
          .catch(err => {
            this.isError = true;
            console.error('Failed to rename project', err);
          });
      });
  }

  /* DELETE */
  async deleteProject(p: any ): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDeletePopupComponent, {
      width: '560px',
      data: {
        onConfirmDelete:async () => {
          await this.http.deleteWithBody('/project', { 

          userId: this.userId,
          projectId: p.projectId,
    
        });
        // delete the project from the list;
        this.projects.set(this.projects().filter((x) => x !== p));
        dialogRef.close();
        },
        onCancel: () => {
          dialogRef.close();
        },
        type: 'Project',
      },
    });
    
  }

  redirectToReport(project:Project | null): void {
    if (!project) return;
    this.router.navigate([`/app/projects/${ project.projectId }/reports`]);
  }

  redirectToReportWithId(proectId:string){
    this.router.navigate([`/app/projects/${ proectId }/reports`]);
  }
}
